'use client';

import { useEffect, useRef } from "react";

// Helper function to send events to Facebook Conversions API
const sendToConversionsAPI = async (eventName: string, customData: Record<string, string | number | boolean> = {}) => {
  const payload = {
    event_name: eventName,
    url: window.location.href,
    custom_data: customData,
    user_data: {
      // User data will be filled when available
    },
    fbclid: new URLSearchParams(window.location.search).get('fbclid') || undefined,
    fbp: document.cookie.match(/_fbp=([^;]+)/)?.[1] || undefined,
    event_time: Math.floor(Date.now() / 1000)
  };

  // Enable detailed logging for debugging
  console.log('🔍 Facebook Pixel: Sending event', { eventName, customData });
  console.log('📊 Browser Pixel tracking active - Conversions API temporarily disabled for debugging');
  
  // TODO: Re-enable Conversions API once worker is fixed
  // URLs would be:
  // - https://backend.certificaciondevalores.com
  // - https://facebook-conversions-api.tudorsabau14.workers.dev
  
  console.log('✅ Browser Pixel event sent successfully');
};

const PixelTracker = () => {
  const scrollDepthsTracked = useRef(new Set<number>());

  useEffect(() => {
    const pixelId = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID;
    
    if (!pixelId || typeof window === 'undefined') {
      if (!pixelId && typeof window !== 'undefined') {
        console.warn('Facebook Pixel ID not found. Please set NEXT_PUBLIC_FACEBOOK_PIXEL_ID in your environment variables.');
      }
      return;
    }

    // Dynamic import to avoid SSR issues
    import('react-facebook-pixel').then((module) => {
      const ReactPixel = module.default;
      ReactPixel.init(pixelId);
      ReactPixel.pageView();
      
      // Also send to Conversions API
      sendToConversionsAPI('PageView', {
        descripcion: 'Usuario visitó la página de certificación',
      });
    });

    // Track scroll depth
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY;
      const scrollPercentage = Math.round((scrolled / scrollHeight) * 100);

      // Track at 25%, 50%, 75%, and 100%
      const thresholds = [25, 50, 75, 100];
      thresholds.forEach(threshold => {
        if (scrollPercentage >= threshold && !scrollDepthsTracked.current.has(threshold)) {
          scrollDepthsTracked.current.add(threshold);
          trackScrollDepth(threshold);
        }
      });
    };

    // Track time on page at 30 seconds and 60 seconds
    const timeTrackers = [
      setTimeout(() => trackTimeOnPage(30), 30000),
      setTimeout(() => trackTimeOnPage(60), 60000),
    ];

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      timeTrackers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  return null;
};

export default PixelTracker;

// Tracking functions for the certification site
export const trackRegistrationStarted = () => {
  if (typeof window !== 'undefined') {
    import('react-facebook-pixel').then((module) => {
      const ReactPixel = module.default;
      ReactPixel.track('InitiateCheckout', {
        content_name: 'Certificación Internacional Coaching X Valores',
        content_category: 'Certificación',
        value: 1495,
        currency: 'EUR',
        descripcion: 'Usuario abrió formulario de registro',
      });
    });
    
    // Also send to Conversions API
    sendToConversionsAPI('InitiateCheckout', {
      content_name: 'Certificación Internacional Coaching X Valores',
      content_category: 'Certificación',
      value: 1495,
      currency: 'EUR',
      descripcion: 'Usuario abrió formulario de registro',
    });
  }
};

export const trackRegistrationCompleted = () => {
  if (typeof window !== 'undefined') {
    import('react-facebook-pixel').then((module) => {
      const ReactPixel = module.default;
      ReactPixel.track('CompleteRegistration', {
        content_name: 'Certificación Internacional Coaching X Valores',
        value: 1495,
        currency: 'EUR',
        status: true,
        descripcion: 'Usuario completó solicitud de admisión',
      });
    });
    
    // Also send to Conversions API
    sendToConversionsAPI('CompleteRegistration', {
      content_name: 'Certificación Internacional Coaching X Valores',
      value: 1495,
      currency: 'EUR',
      status: true,
      descripcion: 'Usuario completó solicitud de admisión',
    });
  }
};

export const trackDossierDownload = () => {
  if (typeof window !== 'undefined') {
    import('react-facebook-pixel').then((module) => {
      const ReactPixel = module.default;
      ReactPixel.track('Lead', {
        content_name: 'Dossier Informativo',
        content_category: 'Descarga',
        descripcion: 'Usuario descargó dossier informativo PDF',
      });
    });
    
    // Also send to Conversions API
    sendToConversionsAPI('Lead', {
      content_name: 'Dossier Informativo',
      content_category: 'Descarga',
      descripcion: 'Usuario descargó dossier informativo PDF',
    });
  }
};

export const trackContactClick = () => {
  if (typeof window !== 'undefined') {
    import('react-facebook-pixel').then((module) => {
      const ReactPixel = module.default;
      ReactPixel.track('Contact', {
        content_name: 'WhatsApp Contact',
        content_category: 'Contacto',
        descripcion: 'Usuario inició contacto por WhatsApp',
      });
    });
    
    // Also send to Conversions API
    sendToConversionsAPI('Contact', {
      content_name: 'WhatsApp Contact',
      content_category: 'Contacto',
      descripcion: 'Usuario inició contacto por WhatsApp',
    });
  }
};

// Track when users view important sections
export const trackViewContent = (sectionName: string) => {
  if (typeof window !== 'undefined') {
    import('react-facebook-pixel').then((module) => {
      const ReactPixel = module.default;
      ReactPixel.track('ViewContent', {
        content_name: sectionName,
        content_category: 'Sección Vista',
        content_type: 'Sección Landing',
      });
    });
    
    // Also send to Conversions API
    sendToConversionsAPI('ViewContent', {
      content_name: sectionName,
      content_category: 'Sección Vista',
      content_type: 'Sección Landing',
    });
  }
};

// Track time spent on page (quality visitors)
export const trackTimeOnPage = (seconds: number) => {
  if (typeof window !== 'undefined') {
    import('react-facebook-pixel').then((module) => {
      const ReactPixel = module.default;
      ReactPixel.trackCustom('TimeOnPage', {
        tiempo_en_pagina: seconds,
        descripcion: `Usuario activo por ${seconds} segundos`,
        calidad_visita: seconds > 30 ? 'Alta' : 'Baja',
      });
    });
    
    // Also send to Conversions API
    sendToConversionsAPI('TimeOnPage', {
      tiempo_en_pagina: seconds,
      descripcion: `Usuario activo por ${seconds} segundos`,
      calidad_visita: seconds > 30 ? 'Alta' : 'Baja',
    });
  }
};

// Track scroll depth
export const trackScrollDepth = (percentage: number) => {
  if (typeof window !== 'undefined') {
    import('react-facebook-pixel').then((module) => {
      const ReactPixel = module.default;
      ReactPixel.trackCustom('ScrollDepth', {
        profundidad_scroll: `${percentage}%`,
        descripcion: `Usuario scrolleó ${percentage}% de la página`,
        engagement_level: percentage >= 75 ? 'Alto' : percentage >= 50 ? 'Medio' : 'Bajo',
      });
    });
    
    // Also send to Conversions API
    sendToConversionsAPI('ScrollDepth', {
      profundidad_scroll: `${percentage}%`,
      descripcion: `Usuario scrolleó ${percentage}% de la página`,
      engagement_level: percentage >= 75 ? 'Alto' : percentage >= 50 ? 'Medio' : 'Bajo',
    });
  }
};

// Track form abandonment
export const trackFormAbandon = (formName: string, fieldsCompleted: number) => {
  if (typeof window !== 'undefined') {
    import('react-facebook-pixel').then((module) => {
      const ReactPixel = module.default;
      ReactPixel.trackCustom('FormAbandon', {
        formulario: formName,
        campos_completados: fieldsCompleted,
        descripcion: `Abandono en ${formName} con ${fieldsCompleted} campos completados`,
      });
    });
    
    // Also send to Conversions API
    sendToConversionsAPI('FormAbandon', {
      formulario: formName,
      campos_completados: fieldsCompleted,
      descripcion: `Abandono en ${formName} con ${fieldsCompleted} campos completados`,
    });
  }
};